/**
 * Portal-wide "pattern fills" (ECharts `aria.decal`) preference.
 *
 * A per-user, not per-widget/per-chart, preference — this is a *how you see
 * charts* setting, not chart configuration, so it lives outside ChartConfig
 * entirely (see the echarts-chart-type-defaults-audit plan's D2).
 *
 * ACCOUNT-SCOPED (plan D2/O2): the control-plane `/api/v1/users/me/preferences`
 * endpoint is the source of truth so the setting roams across a user's
 * machines. `localStorage` is kept only as a same-session write-through
 * cache — it lets the preference apply instantly on the next page load
 * without waiting on a network round trip, but the CP value (once fetched)
 * always wins over whatever was cached locally. Modelled on the same
 * convention PortalChart already follows for light/dark (a class on
 * `<html>`, read live, observed via MutationObserver).
 *
 * DEFAULT (D2/O3): when CP has no explicit preference for this user
 * (`decals_enabled: null`), the default is account-conditional — on for
 * Allan's account specifically (colour-blind, the product owner), off for
 * everyone else — applied client-side each session rather than written back
 * to CP, so it stays a *default*, not a value the user silently "chose".
 */
const DECAL_CLASS = "decal-patterns";
const STORAGE_KEY = "portal:chart-decal-patterns";

/** Accounts that default to decals ON when no explicit CP preference exists. */
const DECAL_DEFAULT_ON_EMAILS = new Set(["allan@larasoft.global"]);

/**
 * True once the effective decal state came from something the user (or
 * their account) actually chose — an explicit CP value, or an in-session
 * toggle click — as opposed to a default or an accessibility auto-enable.
 * Gates the forced-colors/prefers-contrast auto-enable below: it must not
 * override a real choice, but a still-unresolved default is fair game.
 *
 * IMPORTANT: only `persistDecalEnabled` sets this and writes the local
 * cache. `applyDecalEnabled` (used for defaults and the auto-enable itself)
 * deliberately does NEITHER — an account-conditional default that got
 * written to the cache would read back as "explicit" on the next boot
 * (initDecalPreference), permanently defeating the auto-enable for every
 * returning user. Found in gate-review pass 2 (fresh Fable) — see the
 * regression tests below.
 */
let hasExplicitPreference = false;

/**
 * True while a just-fired PUT to CP hasn't resolved yet. Guards
 * syncDecalPreferenceFromAccount against a race: a token refresh (or a
 * second GET) landing between an explicit toggle and its PUT completing
 * must not fetch-and-reapply the pre-toggle CP value over the fresh choice.
 * Found in gate-review pass 2.
 */
let pendingLocalToggle = false;

/** The class-on-`<html>` state PortalChart's MutationObserver watches. */
export function currentDecalEnabled(): boolean {
  return document.documentElement.classList.contains(DECAL_CLASS);
}

/** Fired on every applied change, whatever the source (toggle, CP sync,
 * default, auto-enable) — the single subscription point for anything that
 * needs to stay in lock-step with the preference (e.g. two DecalToggleButton
 * mounts). Found needed in gate-review pass 3: with two mounts, only one of
 * them was ever the one that actually flipped, leaving the other stale. */
export const DECAL_CHANGED_EVENT = "portal:decal-preference-changed";

function writeLocalCache(enabled: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    // Storage unavailable (private browsing, quota) — preference just
    // doesn't survive a reload; the in-session toggle still works.
  }
}

/**
 * Paint the preference onto the DOM only — no cache write, no explicit
 * flag. This is the right primitive for a *default* or an accessibility
 * auto-enable: something that should keep yielding to a later explicit
 * choice, and must not itself be mistaken for one. See `persistDecalEnabled`
 * for the explicit-choice variant.
 */
export function applyDecalEnabled(enabled: boolean): void {
  document.documentElement.classList.toggle(DECAL_CLASS, enabled);
  window.dispatchEvent(
    new CustomEvent(DECAL_CHANGED_EVENT, { detail: { enabled } }),
  );
}

/** Paint the preference AND remember it as an explicit choice (cache +
 * hasExplicitPreference). Use for an in-session toggle or a CP value the
 * account actually holds — never for a default. */
function persistDecalEnabled(enabled: boolean): void {
  hasExplicitPreference = true;
  applyDecalEnabled(enabled);
  writeLocalCache(enabled);
}

function autoEnableActive(): boolean {
  if (typeof window.matchMedia !== "function") return false;
  return (
    window.matchMedia("(forced-colors: active)").matches ||
    window.matchMedia("(prefers-contrast: more)").matches
  );
}

/**
 * Flip the preference: apply it locally immediately (so the toggle feels
 * instant) and fire-and-forget a PUT to CP so it roams. Callers (the toggle
 * button) don't need to await this — a failed sync just means the flip
 * doesn't roam this once, not that the in-session toggle fails.
 */
export function setDecalEnabled(enabled: boolean, token?: string | null): void {
  persistDecalEnabled(enabled);
  if (!token) return;
  pendingLocalToggle = true;
  void import("@/services/api/platform")
    .then(({ platformApi }) =>
      platformApi.updateMyPreferences(token, { decals_enabled: enabled }),
    )
    .catch(() => {
      // Best-effort — the local toggle already applied; a sync failure
      // just means this change doesn't roam to other machines this time.
    })
    .finally(() => {
      pendingLocalToggle = false;
    });
}

/** Restore the local write-through cache onto `<html>` — call once at app
 * start, before auth resolves, so the last-known state paints immediately
 * rather than a decal-off flash while the CP fetch is in flight. The cache
 * only ever holds an explicit choice (persistDecalEnabled is the only
 * writer), so finding a value here is always a genuine prior choice — it
 * must gate the forced-colors/prefers-contrast auto-enable below the same
 * way an in-session toggle or a CP value does, or a returning user's real
 * "off" choice gets silently overridden on every reload under forced-colors
 * until the (async, post-auth) CP sync catches up. */
export function initDecalPreference(): void {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — fall through to the off default.
  }
  if (stored !== null) {
    hasExplicitPreference = true;
  }
  if (stored === "1") {
    document.documentElement.classList.add(DECAL_CLASS);
  }
}

/**
 * Reconcile the local state against the account-scoped CP preference once a
 * session is available. CP's explicit value (true/false) always wins over
 * whatever was cached locally and is remembered as explicit. CP's `null`
 * (never set) resolves to a DEFAULT — Allan's account, OR an active
 * forced-colors/prefers-contrast signal, OR off — applied to the DOM but
 * deliberately NOT persisted and NOT marked explicit, so it keeps yielding
 * to a later real choice (see `hasExplicitPreference`'s doc comment).
 *
 * Two race guards, both found in gate-review (pass 2 and 3):
 * - Checked BEFORE and again AFTER the network round trip: a local toggle
 *   that starts (and finishes) while this GET is in flight must win over
 *   whatever this call was about to apply — checking only at entry leaves
 *   a window between the entry check and the awaited response.
 * - The null-default branch never overrides an ALREADY-explicit local
 *   state (`hasExplicitPreference`) — without this, a locally explicit
 *   choice this session (a toggle whose PUT failed, or fired token-less)
 *   would flip back to the default on every subsequent call, even though
 *   `hasExplicitPreference` correctly says a real choice was already made.
 *
 * Safe to call more than once per session.
 */
export async function syncDecalPreferenceFromAccount(
  token: string,
  email: string | null | undefined,
): Promise<void> {
  if (pendingLocalToggle) return;
  try {
    const { platformApi } = await import("@/services/api/platform");
    const { decals_enabled: stored } = await platformApi.getMyPreferences(token);
    if (pendingLocalToggle) return; // re-check: a toggle may have started mid-request
    // `typeof stored === "boolean"`, not `stored !== null`: the declared
    // return type is `boolean | null`, but a CP response shape that omits
    // the field entirely destructures to `undefined`, and `undefined !==
    // null` is true — that used to reach persistDecalEnabled(undefined),
    // which classList.toggle(DECAL_CLASS, undefined) then FLIPS (toggle's
    // second arg only clears/sets when it's a real boolean) rather than
    // leaves alone, and gets cached as if it were a genuine explicit choice.
    // Anything that isn't a literal true/false — null, undefined, or any
    // other shape a future response might send — falls through to the same
    // default-resolution branch below.
    if (typeof stored === "boolean") {
      persistDecalEnabled(stored);
      return;
    }
    if (hasExplicitPreference) return; // don't override an already-explicit local choice
    const accountDefault = !!email && DECAL_DEFAULT_ON_EMAILS.has(email.toLowerCase());
    applyDecalEnabled(accountDefault || autoEnableActive());
  } catch {
    // CP unreachable — keep whatever the local write-through cache already
    // applied (initDecalPreference's synchronous restore) rather than
    // resetting to off.
  }
}

/**
 * Auto-enable decals when the browser reports `forced-colors: active` or
 * `prefers-contrast: more` — an accessibility signal from the OS/browser,
 * not a portal preference, so it applies regardless of the *default* but
 * never overrides an explicit choice (D2). Call once at app start; the
 * listener stays live for the session so a mid-session OS setting change
 * (e.g. enabling Windows High Contrast) takes effect without a reload.
 */
export function initDecalAutoEnable(): void {
  if (typeof window.matchMedia !== "function") return;
  const forcedColors = window.matchMedia("(forced-colors: active)");
  const highContrast = window.matchMedia("(prefers-contrast: more)");

  const evaluate = () => {
    if (hasExplicitPreference) return;
    if (forcedColors.matches || highContrast.matches) {
      applyDecalEnabled(true);
    }
  };

  evaluate();
  forcedColors.addEventListener("change", evaluate);
  highContrast.addEventListener("change", evaluate);
}
